import {
  useSalesServiceSalesOrdersPartialUpdate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  ContractorType,
  InsuranceTerm,
  OrderDetail,
  OrderEditStatusEnum,
  PossessionBaseEnum,
  PriceGreenCard,
  PriceResponse,
  VehicleType,
  ZoneEnum
} from '@insurance/api/requests'
import { ProductCode } from '@insurance/types'
import { replaceEmptyWithUndefined, showApiErrors } from '@shared/utils'
import { Card, Col, Divider, Form, Modal, Row } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Details } from './Details'
import { GreenCardInsuranceProvider } from './GreenCardInsuranceContext'
import { GreenCardInsuranceControls } from './GreenCardInsuranceControls'
import { InformationStep } from './InformationStep'
import { MainStep } from './MainStep'
import {
  formProps,
  getGreenCardValuesFromStorage,
  normalizeGreenCardData,
  normalizeOrderData,
  removeGreenCardValuesFromStorage,
  saveGreenCardValuesToStorage
} from './utils'

const forms = [<MainStep key={0} />, <InformationStep key={1} />]

const {
  stepKey,
  priceKey,
  orderKey,
  vehicleDataProps,
  vehicleTypeProps,
  zoneProps,
  termProps,
  contractorTypeProps,
  certificateNumberProps,
  trailerCertificateNumberProps,
  validFromProps,
  vehicleSelectedProps,
  identificationNumberProps,
  possessionBaseProps,
  possessionBaseDocNumberProps,
  possessionBaseDocDateProps,
  possessionBaseDocOtherTitleProps,
  possessionBaseDocNoteProps,
  isPropertyKey
} = formProps()

const initValues = {
  [stepKey.name]: 0,
  [priceKey.name]: undefined,
  [orderKey.name]: undefined,
  [vehicleDataProps.name]: undefined,
  [vehicleTypeProps.name]: VehicleType.VEHICLE,
  [zoneProps.name]: ZoneEnum.Z3,
  [termProps.name]: InsuranceTerm.D15,
  [contractorTypeProps.name]: ContractorType.PERSON,
  [certificateNumberProps.name]: undefined,
  [trailerCertificateNumberProps.name]: undefined,
  [validFromProps.name]: undefined,
  [vehicleSelectedProps.name]: undefined,
  [identificationNumberProps.name]: undefined,
  [possessionBaseProps.name]: undefined,
  [possessionBaseDocNumberProps.name]: undefined,
  [possessionBaseDocDateProps.name]: undefined,
  [possessionBaseDocOtherTitleProps.name]: undefined,
  [possessionBaseDocNoteProps.name]: undefined,
  [isPropertyKey.name]: undefined
}

export const GreenCardInsurance = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isReady, setIsReady] = useState(false)

  const { mutate: calculatePrice, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate()

  const { mutate: recalculateOrder, isPending: isRecalculatingLoading } =
    useSalesServiceSalesOrdersPartialUpdate()

  const handleFinishStepError = (error: unknown) => {
    showApiErrors(error, form)
  }

  const updatePriceData = (data: PriceResponse) => {
    const priceData = data as PriceGreenCard
    form.setFieldValue(priceKey.name, {
      amount: Number(Number(priceData.amount)?.toFixed(2))
    })
    form.setFieldValue(vehicleDataProps.name, priceData.data)
    form.setFieldValue(orderKey.name, priceData.order?.id)
    form.setFields(
      form.getFieldsError().map(({ name }) => ({ name, errors: [] }))
    )
  }

  const handleRecalculate = () => {
    const values = form.getFieldsValue(true)
    const orderId = values[orderKey.name] as number | undefined
    if (!orderId) return

    recalculateOrder(
      replaceEmptyWithUndefined({
        id: orderId,
        requestBody: {
          status: OrderEditStatusEnum.DATA_ENTRY,
          stage: 0,
          green_card: normalizeOrderData(values)
        }
      }),
      {
        onSuccess: (order: OrderDetail) => {
          form.setFieldValue(priceKey.name, {
            amount: Number(Number(order.amount)?.toFixed(2))
          })
          form.setFields(
            form.getFieldsError().map(({ name }) => ({ name, errors: [] }))
          )
        },
        onError: (error) =>
          showApiErrors(error, form, { unwrapKeys: ['green_card'] })
      }
    )
  }

  const handleCalculate = async (onSuccess: () => void) => {
    await form.validateFields()
    const values = form.getFieldsValue(true)
    calculatePrice(
      {
        code: ProductCode.GREEN_CARD,
        requestBody: normalizeGreenCardData(values)
      },
      {
        onSuccess: (data) => {
          const priceData = data as PriceGreenCard
          updatePriceData(data)
          form.setFieldValue(isPropertyKey.name, priceData?.is_property)
          form.setFieldValue(
            possessionBaseProps.name,
            priceData.is_property ? PossessionBaseEnum.PROPERTY : undefined
          )
          onSuccess()
        },
        onError: handleFinishStepError
      }
    )
  }

  const handleReadyToSaveData = (changedValues?: Record<string, unknown>) => {
    setIsReady(true)
    if (changedValues) {
      form.setFields(
        Object.keys(changedValues).map((name) => ({ name, errors: [] }))
      )
    }
  }

  const saveOnChangeValue = () => {
    const values = form.getFieldsValue(true)
    saveGreenCardValuesToStorage(values)
    return null
  }

  useEffect(() => {
    const stored = getGreenCardValuesFromStorage()
    form.setFieldsValue({ ...initValues, ...stored })
  }, [form])

  useEffect(() => {
    const stored = getGreenCardValuesFromStorage()
    if (!stored) return

    const modal = Modal.confirm({
      onCancel: () => {
        removeGreenCardValuesFromStorage()
        setIsReady(false)
        form.setFieldsValue(initValues)
      },
      title: t('have_saved_data'),
      onOk: () => setIsReady(true)
    })

    return () => modal.destroy()
  }, [form, t])

  return (
    <GreenCardInsuranceProvider
      isEditable={false}
      isCalculatingLoading={isCalculatingLoading || isRecalculatingLoading}
      handleCalculate={handleCalculate}
      handleRecalculate={handleRecalculate}
    >
      <Form
        layout="vertical"
        form={form}
        onValuesChange={handleReadyToSaveData}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={18}>
            <Form.Item shouldUpdate noStyle>
              {() => <Card>{forms[form.getFieldValue('step')]}</Card>}
            </Form.Item>
          </Col>

          <Col xs={24} lg={6}>
            <Card>
              <Details />
            </Card>
          </Col>
        </Row>

        <Divider />

        <GreenCardInsuranceControls
          steps={forms.length}
          onReadyToSaveData={handleReadyToSaveData}
        />

        {isReady && (
          <Form.Item shouldUpdate noStyle>
            {saveOnChangeValue}
          </Form.Item>
        )}
      </Form>
    </GreenCardInsuranceProvider>
  )
}
